import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Custom hook for location tracking and hourly attendance checking
 * Automatically fetches lab details to get location coordinates
 * @param {Object} params - Parameters for tracking
 * @param {string} params.assessorId - Assessor ID
 * @param {string} params.appId - Application ID
 * @param {string} params.orgId - Organization ID (optional, defaults to assessorId for lab details API)
 * @returns {Object} - { locationWarningOpen, distanceMessage, attendanceCheckDialogOpen, attendanceCheckMessage, setLocationWarningOpen, setAttendanceCheckDialogOpen, checkHourlyAttendance, labData, fetchingLabData }
 */
const useAssessmentTracking = ({
  assessorId,
  appId,
  orgId,
} = {}) => {
  // Lab data state
  const [labData, setLabData] = useState({});
  const [fetchingLabData, setFetchingLabData] = useState(false);
  
  // Geolocation tracking states
  const [locationWarningOpen, setLocationWarningOpen] = useState(false);
  const [distanceMessage, setDistanceMessage] = useState("");
  
  // Hourly Attendance Check Dialog state
  const [attendanceCheckDialogOpen, setAttendanceCheckDialogOpen] = useState(false);
  const [attendanceCheckMessage, setAttendanceCheckMessage] = useState("");
  const [locationCheck, setLocationCheck] = useState("");
  const [applicationStage, setApplicationStage] = useState(null);

  // Fetch lab details from API
  const fetchLabDetails = async () => {
    if (!appId || !assessorId) {
      console.log('⚠️ Missing appId or assessorId for lab details fetch');
      return;
    }

    try {
      setFetchingLabData(true);
      const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getlabdetails`;
      const params = {
        appid: appId,
        orgid: assessorId,
      };
      const response = await axios.get(apiUrl, { params });
      
      if (response.data && response.data.isstatus) {
        const lab = response.data;
        setLabData({
          assessmentId: lab.assessmentId || "",
          labLatitude: lab.labLatitude || "",
          labLongitude: lab.labLongitude || "",
          applatitude: lab.applatitude || "",
          applongitude: lab.applongitude || "",
          appAddress: lab.appAddress || "",
          startAssessment: lab.startAssessment === "true" ? true : false,
          stopAssessment: lab.stopAssessment === "true" ? true : false,
        });
        setLocationCheck(lab.location || "");
        console.log('✅ Lab details fetched successfully');
      } else {
        console.warn('⚠️ API response isstatus is false');
      }
    } catch (error) {
      console.error('❌ Error fetching lab details:', error);
    } finally {
      setFetchingLabData(false);
    }
  };

  // Fetch lab details on mount
  useEffect(() => {
    fetchLabDetails();
  }, [appId, assessorId]);

  // Calculate distance between two coordinates using Haversine formula (in km)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  // Check if user is within 1km of lab/app location
  const checkLocationProximity = () => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      return;
    }

    // Check against lab coordinates
    const labLat = parseFloat(labData.labLatitude);
    const labLon = parseFloat(labData.labLongitude);
    
    // Check against app coordinates
    const appLat = parseFloat(labData.applatitude);
    const appLon = parseFloat(labData.applongitude);

    let isOutOfRange = false;
    let distanceMsg = '';

    // Check distance between lab and app coordinates (they should be within 1km of each other)
    if (labLat && labLon && !isNaN(labLat) && !isNaN(labLon) && 
        appLat && appLon && !isNaN(appLat) && !isNaN(appLon)) {
      
      const labToAppDistance = calculateDistance(labLat, labLon, appLat, appLon);
      console.log(`Distance between Lab and App coordinates: ${labToAppDistance.toFixed(2)} km`);
      
      if (labToAppDistance > 1) {
        isOutOfRange = true;
        distanceMsg = `The laboratory and mobile application locations are ${labToAppDistance.toFixed(2)} km apart. The allowed distance is within 1 km.`;
        setDistanceMessage(distanceMsg);
      }
    }

    // Show warning if out of range
    if (isOutOfRange) {
      console.warn('⚠️ Lab and App locations are more than 1km apart!');
      if(locationCheck==="0" || locationCheck===""){
        setLocationWarningOpen(true);
      }else{
        setLocationWarningOpen(false);
      }
    } else {
      console.log('✅ Lab and App locations are within acceptable range');
      setLocationWarningOpen(false);
    }
  };

  const fetchlabstagedetails = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`;

       const response = await fetch(apiUrl);
      const result = await response.json();
       if (result.success) {
        setApplicationStage(result.stage);
        console.log('Sidebar - Application stage loaded:', result.stage);
        return result.stage; // Return the stage value
      } else {
        setApplicationStage('1'); // Default to 1
        console.log('Sidebar - Application stage API response:', result);
        return '1'; // Return default
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage('1'); // Default to 1
      return '1'; // Return default
    }
  };
  // Check Hourly Attendance
  const checkHourlyAttendance = async () => {
    if (!assessorId || !labData.assessmentId || !appId || !orgId) {
      console.log('⚠️ Missing parameters for hourly attendance check');
      return;
    }

    try {
      // First fetch the current application stage and get the returned value
      const currentStage = await fetchlabstagedetails();
      console.log('Current application stage:', currentStage);
      
      // Format current date as DD-MM-YYYY
      const now = new Date();
      const datetime = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ` +
                 `${String(now.getHours()).padStart(2, '0')}:` +
                 `${String(now.getMinutes()).padStart(2, '0')}:` +
                 `${String(now.getSeconds()).padStart(2, '0')}`;
      
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/HourlyAttendenceCheck?assessorId=${assessorId}&assessmentId=${labData.assessmentId}&appId=${appId}&orgId=${orgId}&datetime=${datetime}`;
      
      const response = await axios.get(url);

      if (!response.data.isstatus) {
        // Show dialog if attendance not marked AND stage is 20
        setAttendanceCheckMessage(response.data.message || "Please mark hourly attendance from mobile app");
        if(currentStage === '20') {
          setAttendanceCheckDialogOpen(true);
          console.log('⚠️ Hourly attendance not marked - showing dialog (stage 20)');
        } else {
          console.log(`⚠️ Hourly attendance not marked but not showing dialog (stage: ${currentStage})`);
        }
      } else {
        console.log('✅ Hourly attendance already marked');
      }
    } catch (error) {
      console.error("❌ Error checking hourly attendance:", error);
    }
  };

  // Setup location tracking on component mount
  useEffect(() => {
    // Only start tracking if lab data has coordinates
    if (labData.labLatitude || labData.labLongitude || labData.applatitude || labData.applongitude) {
      console.log('🌍 Starting location tracking...');
      
      // Check immediately on mount
      checkLocationProximity();
      
      // Set up interval to check every 15 minutes (900000 ms)
      const intervalId = setInterval(() => {
        console.log('⏰ Running scheduled location check...');
        checkLocationProximity();
      }, 900000); // 15 minutes = 15 * 60 * 1000 = 900000 ms
      
      // Cleanup on unmount
      return () => {
        console.log('🛑 Stopping location tracking...');
        clearInterval(intervalId);
      };
    }
  }, [labData.labLatitude, labData.labLongitude, labData.applatitude, labData.applongitude]);

  // Setup hourly attendance check every 1 hour
  useEffect(() => {
    // Only start checking if we have all required parameters and assessment is started
    if (labData.assessmentId && appId && orgId && assessorId && labData.startAssessment && !labData.stopAssessment) {
      console.log('⏰ Setting up hourly attendance check every 1 hour');
      
      // Check immediately on mount
      checkHourlyAttendance();
      //fetchlabstagedetails();
      // Set up interval to check every 1 hour (3600000 ms)
      const attendanceInterval = setInterval(() => {
        console.log('⏰ Running scheduled hourly attendance check...');
        checkHourlyAttendance();
        //fetchlabstagedetails();
      }, 3600000); // 1 hour = 60 * 60 * 1000 = 3600000 ms

      // Cleanup on unmount
      return () => {
        console.log('🛑 Clearing hourly attendance check interval');
        clearInterval(attendanceInterval);
      };
    }
  }, [labData.assessmentId, appId, orgId, assessorId, labData.startAssessment, labData.stopAssessment]);

  return {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
    fetchlabstagedetails,
    labData,
    fetchingLabData,
  };
};

export default useAssessmentTracking;



// import { useState, useEffect } from 'react';
// import axios from 'axios';

// /**
//  * Custom hook for location tracking and hourly attendance checking
//  * Automatically fetches lab details and facility locations to get location coordinates
//  * @param {Object} params - Parameters for tracking
//  * @param {string} params.assessorId - Assessor ID
//  * @param {string} params.appId - Application ID
//  * @param {string} params.orgId - Organization ID (optional, defaults to assessorId for lab details API)
//  * @returns {Object} - { locationWarningOpen, distanceMessage, attendanceCheckDialogOpen, attendanceCheckMessage, setLocationWarningOpen, setAttendanceCheckDialogOpen, checkHourlyAttendance, fetchlabstagedetails, labData, fetchingLabData, facilityLocations, fetchingFacilityLocations }
//  */
// const useAssessmentTracking = ({
//   assessorId,
//   appId,
//   orgId,
// } = {}) => {
//   // Lab data state
//   const [labData, setLabData] = useState({});
//   const [fetchingLabData, setFetchingLabData] = useState(false);
  
//   // Facility locations state
//   const [facilityLocations, setFacilityLocations] = useState([]);
//   const [fetchingFacilityLocations, setFetchingFacilityLocations] = useState(false);
  
//   // Geolocation tracking states
//   const [locationWarningOpen, setLocationWarningOpen] = useState(false);
//   const [distanceMessage, setDistanceMessage] = useState("");
  
//   // Hourly Attendance Check Dialog state
//   const [attendanceCheckDialogOpen, setAttendanceCheckDialogOpen] = useState(false);
//   const [attendanceCheckMessage, setAttendanceCheckMessage] = useState("");
//   const [locationCheck, setLocationCheck] = useState("");
//   const [applicationStage, setApplicationStage] = useState(null);

//   // Fetch lab details from API
//   const fetchLabDetails = async () => {
//     if (!appId || !assessorId) {
//       console.log('⚠️ Missing appId or assessorId for lab details fetch');
//       return;
//     }

//     try {
//       setFetchingLabData(true);
//       const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getlabdetails`;
//       const params = {
//         appid: appId,
//         orgid: assessorId,
//       };
//       const response = await axios.get(apiUrl, { params });
      
//       if (response.data && response.data.isstatus) {
//         const lab = response.data;
//         setLabData({
//           assessmentId: lab.assessmentId || "",
//           labLatitude: lab.labLatitude || "",
//           labLongitude: lab.labLongitude || "",
//           applatitude: lab.applatitude || "",
//           applongitude: lab.applongitude || "",
//           appAddress: lab.appAddress || "",
//           startAssessment: lab.startAssessment === "true" ? true : false,
//           stopAssessment: lab.stopAssessment === "true" ? true : false,
//         });
//         setLocationCheck(lab.location || "");
//         console.log('✅ Lab details fetched successfully');
//       } else {
//         console.warn('⚠️ API response isstatus is false');
//       }
//     } catch (error) {
//       console.error('❌ Error fetching lab details:', error);
//     } finally {
//       setFetchingLabData(false);
//     }
//   };

//   // Fetch lab details on mount
//   useEffect(() => {
//     fetchLabDetails();
//   }, [appId, assessorId]);

//   // Fetch facility locations from API
//   const fetchFacilityLocations = async () => {
//     if (!appId || !orgId) {
//       console.log('⚠️ Missing appId or orgId for facility locations fetch');
//       return;
//     }

//     try {
//       setFetchingFacilityLocations(true);
//       const apiUrl = `${process.env.REACT_APP_API_URL}/calibration-laboratory/facility-locations/${appId}/${orgId}`;
//       const response = await axios.get(apiUrl);
//       if (response.data && response.data.success && response.data.data) {
//         // Filter only approved facility locations (isApprove === 1)
//         const approvedLocations = response.data.data.filter(loc => loc.isApprove === 1);
//         setFacilityLocations(approvedLocations);
//         console.log(`✅ Fetched ${approvedLocations.length} approved facility locations`);
//       } else {
//         console.warn('⚠️ No facility locations found or API response unsuccessful');
//         setFacilityLocations([]);
//       }
//     } catch (error) {
//       console.error('❌ Error fetching facility locations:', error);
//       setFacilityLocations([]);
//     } finally {
//       setFetchingFacilityLocations(false);
//     }
//   };

//   // Fetch facility locations on mount
//   useEffect(() => {
//     fetchFacilityLocations();
//   }, [appId, orgId]);

//   // Calculate distance between two coordinates using Haversine formula (in km)
//   const calculateDistance = (lat1, lon1, lat2, lon2) => {
//     const R = 6371; // Earth's radius in kilometers
//     const dLat = (lat2 - lat1) * Math.PI / 180;
//     const dLon = (lon2 - lon1) * Math.PI / 180;
//     const a = 
//       Math.sin(dLat / 2) * Math.sin(dLat / 2) +
//       Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
//       Math.sin(dLon / 2) * Math.sin(dLon / 2);
//     const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
//     const distance = R * c;
//     return distance;
//   };

//   // Check if user is within 1km of lab/app location or any facility location
//   const checkLocationProximity = () => {
//     if (!navigator.geolocation) {
//       console.warn("Geolocation is not supported by this browser.");
//       return;
//     }
//     const appLat = parseFloat(labData.applatitude);
//     const appLon = parseFloat(labData.applongitude);
//     let isWithinRange = false;
//     let closestDistance = Infinity;
//     // Check Lab Coordinates
//     const labLat = parseFloat(labData.labLatitude);
//     const labLon = parseFloat(labData.labLongitude);

//     if (!isNaN(labLat) && !isNaN(labLon) && !isNaN(appLat) && !isNaN(appLon)) {
//       const distance = calculateDistance(appLat, appLon, labLat, labLon);

//       closestDistance = Math.min(closestDistance, distance);

//       if (distance <= 1) {
//         isWithinRange = true;
//       }
//     }

//     // Check Facility Locations only if Lab is not within range
//     if (!isWithinRange && facilityLocations.length > 0) {
//       facilityLocations.forEach((facility) => {
//         const facilityLat = parseFloat(facility.Latitude);
//         const facilityLon = parseFloat(facility.Longitude);

//         if (!isNaN(facilityLat) && !isNaN(facilityLon)) {
//           const distance = calculateDistance(
//             appLat,
//             appLon,
//             facilityLat,
//             facilityLon,
//           );

//           closestDistance = Math.min(closestDistance, distance);

//           if (distance <= 1) {
//             isWithinRange = true;
//           }
//         }
//       });
//     }

//     // Final Result
//     if (!isWithinRange) {
//       setDistanceMessage(`The laboratory and mobile application locations are ${closestDistance.toFixed(2)} km apart. The allowed distance is within 1 km.`);
//       if (locationCheck === "0" || locationCheck === "") {
//         setLocationWarningOpen(true);
//       } else {
//         setLocationWarningOpen(false);
//       }
//     } else {
//       setLocationWarningOpen(false);
//     }
//   };

//   const fetchlabstagedetails = async () => {
//     try {
//       const apiUrl = `${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`;

//        const response = await fetch(apiUrl);
//       const result = await response.json();
//        if (result.success) {
//         setApplicationStage(result.stage);
//         console.log('Sidebar - Application stage loaded:', result.stage);
//         return result.stage; // Return the stage value
//       } else {
//         setApplicationStage('1'); // Default to 1
//         console.log('Sidebar - Application stage API response:', result);
//         return '1'; // Return default
//       }
//     } catch (error) {
//       console.error('Error fetching application stage:', error);
//       setApplicationStage('1'); // Default to 1
//       return '1'; // Return default
//     }
//   };
//   // Check Hourly Attendance
//   const checkHourlyAttendance = async () => {
//     if (!assessorId || !labData.assessmentId || !appId || !orgId) {
//       console.log('⚠️ Missing parameters for hourly attendance check');
//       return;
//     }

//     try {
//       // First fetch the current application stage and get the returned value
//       const currentStage = await fetchlabstagedetails();
//       console.log('Current application stage:', currentStage);
      
//       // Format current date as DD-MM-YYYY
//       const now = new Date();
//       const datetime = `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ` +
//                  `${String(now.getHours()).padStart(2, '0')}:` +
//                  `${String(now.getMinutes()).padStart(2, '0')}:` +
//                  `${String(now.getSeconds()).padStart(2, '0')}`;
      
//       const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/HourlyAttendenceCheck?assessorId=${assessorId}&assessmentId=${labData.assessmentId}&appId=${appId}&orgId=${orgId}&datetime=${datetime}`;
      
//       const response = await axios.get(url);

//       if (!response.data.isstatus) {
//         // Show dialog if attendance not marked AND stage is 20
//         setAttendanceCheckMessage(response.data.message || "Please mark hourly attendance from mobile app");
//         if(currentStage === '20') {
//           setAttendanceCheckDialogOpen(true);
//           console.log('⚠️ Hourly attendance not marked - showing dialog (stage 20)');
//         } else {
//           console.log(`⚠️ Hourly attendance not marked but not showing dialog (stage: ${currentStage})`);
//         }
//       } else {
//         console.log('✅ Hourly attendance already marked');
//       }
//     } catch (error) {
//       console.error("❌ Error checking hourly attendance:", error);
//     }
//   };

//   // Setup location tracking on component mount
//   useEffect(() => {
//     // Only start tracking if lab data has coordinates OR facility locations exist
//     if (labData.labLatitude || labData.labLongitude || labData.applatitude || labData.applongitude || facilityLocations.length > 0) {
//       console.log('🌍 Starting location tracking...');
      
//       // Check immediately on mount
//       checkLocationProximity();
      
//       // Set up interval to check every 15 minutes (900000 ms)
//       const intervalId = setInterval(() => {
//         console.log('⏰ Running scheduled location check...');
//         checkLocationProximity();
//       }, 900000); // 15 minutes = 15 * 60 * 1000 = 900000 ms
      
//       // Cleanup on unmount
//       return () => {
//         console.log('🛑 Stopping location tracking...');
//         clearInterval(intervalId);
//       };
//     }
//   }, [labData.labLatitude, labData.labLongitude, labData.applatitude, labData.applongitude, facilityLocations]);

//   // Setup hourly attendance check every 1 hour
//   useEffect(() => {
//     // Only start checking if we have all required parameters and assessment is started
//     if (labData.assessmentId && appId && orgId && assessorId && labData.startAssessment && !labData.stopAssessment) {
//       console.log('⏰ Setting up hourly attendance check every 1 hour');
      
//       // Check immediately on mount
//       checkHourlyAttendance();
//       //fetchlabstagedetails();
//       // Set up interval to check every 1 hour (3600000 ms)
//       const attendanceInterval = setInterval(() => {
//         console.log('⏰ Running scheduled hourly attendance check...');
//         checkHourlyAttendance();
//         //fetchlabstagedetails();
//       }, 3600000); // 1 hour = 60 * 60 * 1000 = 3600000 ms

//       // Cleanup on unmount
//       return () => {
//         console.log('🛑 Clearing hourly attendance check interval');
//         clearInterval(attendanceInterval);
//       };
//     }
//   }, [labData.assessmentId, appId, orgId, assessorId, labData.startAssessment, labData.stopAssessment]);

//   return {
//     locationWarningOpen,
//     distanceMessage,
//     attendanceCheckDialogOpen,
//     attendanceCheckMessage,
//     setLocationWarningOpen,
//     setAttendanceCheckDialogOpen,
//     checkHourlyAttendance,
//     fetchlabstagedetails,
//     labData,
//     fetchingLabData,
//     facilityLocations,
//     fetchingFacilityLocations,
//   };
// };

// export default useAssessmentTracking;



